import React, { useEffect, useRef, useState } from 'react';

interface ChatLiveAnnouncerProps {
  /** True while an assistant reply is still streaming in. */
  isActivelyStreaming: boolean;
  /** Element that contains the rendered message list. */
  scopeRef: React.RefObject<HTMLElement | null>;
}

/** Visually hidden, still exposed to assistive tech. */
const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
};

/**
 * Announces a completed assistant reply to screen readers exactly once.
 *
 * The transcript itself is `aria-live="off"` (see `Conversation`): streaming
 * text mutates on every token, and a live transcript makes assistive tech
 * restart the readout each time — the reason a tester could never hear a
 * response through to the end. This component stays silent while tokens land,
 * then snapshots the *rendered* text of the finished reply, so the readout
 * matches what is on screen rather than the raw markdown behind it.
 */
export const ChatLiveAnnouncer: React.FC<ChatLiveAnnouncerProps> = ({ isActivelyStreaming, scopeRef }) => {
  const [announcement, setAnnouncement] = useState('');
  const wasStreamingRef = useRef(false);

  useEffect(() => {
    if (isActivelyStreaming) {
      wasStreamingRef.current = true;
      return;
    }
    // Only announce a stream that actually ran in this session — mounting on an
    // existing conversation must not read its last reply out of nowhere.
    if (!wasStreamingRef.current) return;
    wasStreamingRef.current = false;

    // One frame of slack so the final tokens are committed before we snapshot.
    const raf = requestAnimationFrame(() => {
      const assistantMessages = scopeRef.current?.querySelectorAll('.message.assistant');
      const last = assistantMessages?.[assistantMessages.length - 1] as HTMLElement | undefined;
      const text = last?.innerText?.trim();
      if (text) setAnnouncement(text);
    });

    return () => cancelAnimationFrame(raf);
  }, [isActivelyStreaming, scopeRef]);

  return (
    <div aria-live="polite" aria-atomic="true" style={visuallyHidden}>
      {announcement}
    </div>
  );
};
